/**
 * The two session endings that must NOT bounce the operator to sign-in (H §4.5):
 *
 *  - `revoked` — `RESELLER_ACCESS_REVOKED` (a 401 from saas-auth's refresh, or a 403 from
 *    tmm-api's live member check): the member was removed or suspended, or the reseller was
 *    suspended. The session is already cleared; this is a signed-out screen with NO
 *    auto-redirect — signing straight back in would only fail again.
 *  - `misconfigured` — `REFRESH_ORIGIN_UNROUTABLE` / `INVALID_ORIGIN`: the host is not wired
 *    to saas-auth correctly. The code was logged to the console by `client.ts`.
 *
 * Rendered in place by `AuthGate` / `AuthReturn`. Unbranded, like the other boot-level screens.
 */
import { useTranslation } from 'react-i18next';
import { SkipLink } from '@/components/SkipLink';
import { redirectToCentralizedLogin } from '@/auth/silentRefresh';
import { HOME_PATH } from '@/routes/home';
import { blankSlateStyle } from './blankSlateStyle';

export function SessionEnded({ reason }: { reason: 'revoked' | 'misconfigured' }) {
  const { t } = useTranslation();
  return (
    <>
      <SkipLink />
      <main id="main" tabIndex={-1} style={blankSlateStyle}>
        <div role="alert">
          <h1>{t(`auth.${reason}Title`)}</h1>
          <p style={{ maxWidth: '32rem' }}>{t(`auth.${reason}Body`)}</p>
        </div>
        {reason === 'revoked' ? (
          <button type="button" onClick={() => redirectToCentralizedLogin(HOME_PATH)}>
            {t('auth.signInAgain')}
          </button>
        ) : null}
      </main>
    </>
  );
}
